/*
 * Freakmount — mobile sticky Add to Cart (WIL-115).
 *
 * Reveals a viewport-fixed CTA whenever the in-page Add to Cart is off-screen and
 * hides it while that button is visible, so the two never show at once. The sticky
 * button submits the existing product <form> through its `form` attribute, so all
 * cart behaviour comes from product-form.js — this element only handles the reveal
 * animation and mirrors the in-page button's disabled state and label.
 */
if (!customElements.get('fm-sticky-atc')) {
  customElements.define(
    'fm-sticky-atc',
    class FmStickyAtc extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.hidden = false; // was `hidden` for the no-JS fallback / no flash on load
        this.button = this.querySelector('.fm-sticky-atc__button');
        this.label = this.querySelector('.fm-sticky-atc__label');

        // The in-page Add to Cart: our reveal sentinel and the source of truth for state.
        this.source = document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);
        if (!this.button || !this.label || !this.source) return;

        this.syncState = this.syncState.bind(this);
        this.syncState();

        // Mirror disabled state + label as the variant (and availability) changes.
        this.stateObserver = new MutationObserver(this.syncState);
        this.stateObserver.observe(this.source, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        // Reveal only once the in-page Add to Cart has scrolled ABOVE the viewport
        // (the user is now below it). Stay hidden at the top of the page, where the
        // button is still below the fold and hasn't been reached yet.
        this.visibilityObserver = new IntersectionObserver(
          ([entry]) => {
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.classList.toggle('is-visible', scrolledPast);
          },
          { threshold: 0 }
        );
        this.visibilityObserver.observe(this.source);
      }

      syncState() {
        const disabled =
          this.source.hasAttribute('disabled') || this.source.getAttribute('aria-disabled') === 'true';
        this.button.disabled = disabled;

        const sourceLabel = this.source.querySelector('span');
        const text = sourceLabel && sourceLabel.textContent.trim();
        if (text && this.label.textContent.trim() !== text) this.label.textContent = text;
      }

      disconnectedCallback() {
        this.stateObserver?.disconnect();
        this.visibilityObserver?.disconnect();
        this.initialized = false;
      }
    }
  );
}
